/**
 * Um quadro repetido do posto: título, tabela, vazio que ensina, erro com "tentar de novo" e o botão de
 * adicionar. As etapas montam as colunas e os diálogos; o desenho do quadro é igual em todas.
 */
import React from 'react';
import { Alert, Box, Button, IconButton, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataTable, type DataTableColumn } from '../../../../components';
import { MainText } from '../../MainText';

interface ItemsBlockProps<T extends { id: number }> {
  title: string;
  description?: string;
  items: T[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  columns: DataTableColumn<T>[];
  /** O que o quadro vazio ensina a cadastrar, para quem pode cadastrar (literal na etapa, para o teste de textos de vazio auditar). */
  emptyState: { title: string; description: string };
  addLabel: string;
  /** Nome do item no singular, para os rótulos das ações ("Editar atividade"). */
  itemName: string;
  /** Texto curto que identifica a linha ('Editar atividade: Digitação'): o nome acessível de cada ação. */
  itemLabel: (row: T) => string;
  canEdit: boolean;
  onAdd: () => void;
  onEdit: (row: T) => void;
  onRemove: (row: T) => void;
}

const LABEL_LIMIT = 40;
const TOUCH_TARGET = 44;

const shortLabel = (text: string): string =>
  text.length > LABEL_LIMIT ? `${text.slice(0, LABEL_LIMIT).trimEnd()}…` : text;

export function ItemsBlock<T extends { id: number }>({
  title,
  description,
  items,
  loading,
  error,
  onRetry,
  columns,
  emptyState,
  addLabel,
  itemName,
  itemLabel,
  canEdit,
  onAdd,
  onEdit,
  onRemove,
}: ItemsBlockProps<T>): React.ReactElement {
  // Alvos de 44px: o `actions` do DataTable é comum e fixa um ícone pequeno, então a coluna de ações é daqui.
  const mainColumns: DataTableColumn<T>[] = columns.map((column) => ({
    ...column,
    render: (row: T) => <MainText>{column.render(row)}</MainText>,
  }));
  const columnsWithActions: DataTableColumn<T>[] = canEdit
    ? [
        ...mainColumns,
        {
          id: 'actions',
          label: 'Ações',
          align: 'center',
          render: (row) => (
            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
              <IconButton
                aria-label={`Editar ${itemName}: ${shortLabel(itemLabel(row))}`}
                onClick={() => onEdit(row)}
                sx={{ width: TOUCH_TARGET, height: TOUCH_TARGET }}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                color="error"
                aria-label={`Remover ${itemName}: ${shortLabel(itemLabel(row))}`}
                onClick={() => onRemove(row)}
                sx={{ width: TOUCH_TARGET, height: TOUCH_TARGET }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ),
        },
      ]
    : mainColumns;

  // Repassado por spread: o texto de cada etapa é literal na etapa, onde o teste de textos de vazio o audita.
  const emptyTableProps = {
    emptyState: {
      title: emptyState.title,
      description: canEdit
        ? emptyState.description
        : 'Ainda não há nada registrado aqui. Quem tem acesso para editar o posto pode cadastrar.',
    },
  };

  return (
    <Box component="section" sx={{ mb: 5 }}>
      <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body1" sx={{ mb: 2 }}>
          {description}
        </Typography>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ fontSize: '1rem', mb: 2 }}
          action={
            <Button color="inherit" onClick={onRetry} sx={{ minHeight: 44 }}>
              Tentar de novo
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {!error && (
        <DataTable<T>
          embedded
          loading={loading}
          rows={items}
          getRowId={(row) => row.id}
          columns={columnsWithActions}
          {...emptyTableProps}
        />
      )}

      {canEdit && (
        <Button variant="outlined" onClick={onAdd} sx={{ mt: 2, minHeight: 44 }}>
          {addLabel}
        </Button>
      )}
    </Box>
  );
}
